import React from 'react'
import Calculator from '@/components/Calculator'
import {Wrench, AlertTriangle} from 'lucide-react'

export default function RepairMaintenancePage() {
  const repairTypes = [
    { service: 'Roof Leak Repair', cost: '$300 - $1,200', timeframe: 'Same day' },
    { service: 'Shingle Replacement (per square)', cost: '$150 - $400', timeframe: '1-2 hours' },
    { service: 'Flashing Repair', cost: '$200 - $800', timeframe: '2-4 hours' },
    { service: 'Roof Tear Off (per square)', cost: '$100 - $150', timeframe: 'Labor only' },
    { service: 'Skylight Installation', cost: '$800 - $2,500', timeframe: '4-8 hours' },
    { service: 'Gutter Replacement', cost: '$600 - $1,800', timeframe: '1 day' },
  ]

  const warningSign = [
    'Water stains on ceilings or walls',
    'Missing, cracked, or curling shingles',
    'Granules in gutters (shingle deterioration)',
    'Sagging roof deck',
    'Daylight visible through roof boards',
    'Higher than normal energy bills',
  ]

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-700 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Wrench className="text-white mx-auto mb-4" size={56} />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Roof Repair & Labor Cost Estimator
            </h1>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              Get instant roof repair estimates near me. Calculate average roofing labor cost for leak repairs, shingle replacement, tear off, and skylight installation.
            </p>
          </div>

          <div className="flex justify-center">
            <Calculator materialType="repair" title="Roof Repair Cost Calculator" />
          </div>
        </div>
      </section>

      {/* Average Roofing Labor Cost */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            Average Roofing Labor Cost by Service
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-orange-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bold">Repair Service</th>
                  <th className="px-6 py-4 text-left font-bold">Cost Range</th>
                  <th className="px-6 py-4 text-left font-bold">Timeframe</th>
                </tr>
              </thead>
              <tbody>
                {repairTypes.map((repair, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-semibold text-gray-900">{repair.service}</td>
                    <td className="px-6 py-4 text-orange-600 font-bold">{repair.cost}</td>
                    <td className="px-6 py-4 text-gray-700">{repair.timeframe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Tear Off Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Roof Tear Off Cost Calculator
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Roof tear off (removal of existing shingles) is a critical part of roof replacement. Labor costs depend on the number of existing layers, material type, and disposal fees.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-800 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-xl text-gray-900 mb-3">Tear Off Pricing</h3>
                <p className="text-gray-700">
                  <strong>Single Layer:</strong> $100 - $150 per square (100 sq ft)<br />
                  <strong>Double Layer:</strong> $125 - $175 per square<br />
                  <strong>Triple Layer:</strong> $150 - $200 per square
                </p>
              </div>

              <p className="text-gray-600">
                <em>Note: Some building codes require complete tear off before re-roofing. Overlaying new shingles is cheaper but not always permitted.</em>
              </p>
            </div>

            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Skylight Price Calculator
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Skylight installation adds natural light and value to your home. Costs vary by size, glazing type, and whether it's a deck-mounted or curb-mounted unit.
              </p>

              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                  <h4 className="font-bold text-lg text-gray-900">Fixed Skylight</h4>
                  <p className="text-orange-600 font-bold text-2xl mt-2">$800 - $1,500</p>
                  <p className="text-gray-600 text-sm mt-2">Non-opening, basic glazing, standard size</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                  <h4 className="font-bold text-lg text-gray-900">Ventilating Skylight</h4>
                  <p className="text-orange-600 font-bold text-2xl mt-2">$1,200 - $2,000</p>
                  <p className="text-gray-600 text-sm mt-2">Manual or electric opening, improved ventilation</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                  <h4 className="font-bold text-lg text-gray-900">Solar-Powered Skylight</h4>
                  <p className="text-orange-600 font-bold text-2xl mt-2">$1,500 - $2,500</p>
                  <p className="text-gray-600 text-sm mt-2">Smart controls, rain sensors, energy-efficient</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-orange-50 border-2 border-orange-500 rounded-xl p-8">
            <div className="flex items-center mb-6">
              <AlertTriangle className="text-orange-500 mr-3" size={40} />
              <h2 className="text-3xl font-bold text-gray-900">
                When to Get a Roof Repair Estimate Near Me
              </h2>
            </div>

            <p className="text-lg text-gray-700 mb-6">
              Don't wait until a small problem becomes a costly emergency. Watch for these warning signs:
            </p>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {warningSign.map((sign, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-orange-500 font-bold mr-2">•</span>
                  <span className="text-gray-700">{sign}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

    </div>
  )
}
